import { ReactNode, useLayoutEffect, useRef } from 'react';

import {
    BRAND_HEADER_ATTR,
    CHROME_RADIUS,
    HEADER_BOX_HEIGHT_VAR,
    HEADER_BOX_LEFT_VAR,
    HEADER_BOX_TOP_VAR,
    HEADER_SHIFT_LEFT_VAR,
    HEADER_SHIFT_RIGHT_VAR,
    HEADER_SHIFT_TOP_VAR
} from './headerLayout';
import { brand } from '../../branding';

// Marks <html> while the page is fitted, so App.css can switch off the document's own scrolling
// (see `.pih-fixed-chrome`). Never set otherwise - a route without a FitToViewport (the 404 page,
// say) keeps scrolling normally instead of being clipped.
const ROOT_CLASS = 'pih-fixed-chrome';

// Inline properties this component may write on an ancestor; all restored to their previous value
// on undo, so an inline style the layout itself had set is never lost.
const PROPS = [
    'maxHeight',
    'maxWidth',
    'minHeight',
    'minWidth',
    'boxSizing',
    'overflowY',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'marginTop',
    'marginRight',
    'marginBottom',
    'marginLeft',
    'backgroundColor',
    'visibility',
    'borderTopLeftRadius',
    'borderTopRightRadius',
    'borderBottomLeftRadius',
    'borderBottomRightRadius'
] as const;
type Prop = (typeof PROPS)[number];
type Saved = { node: HTMLElement; before: Record<Prop, string> };

const px = (value: string) => parseFloat(value) || 0;

const isScrollContainer = (node: HTMLElement) => {
    const { overflowY } = getComputedStyle(node);
    return overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
};

const overflowOf = (node: Element) => [node.scrollWidth - node.clientWidth, node.scrollHeight - node.clientHeight];

const SHIFT_VARS = [
    HEADER_SHIFT_LEFT_VAR,
    HEADER_SHIFT_TOP_VAR,
    HEADER_SHIFT_RIGHT_VAR,
    HEADER_BOX_LEFT_VAR,
    HEADER_BOX_TOP_VAR,
    HEADER_BOX_HEIGHT_VAR
];

// The blueprint's DOKA yellow (#FEDC00), with some slack for anti-aliasing and theme variants.
const isDokaYellow = (color: string) => {
    const [r, g, b] = (color.match(/\d+(\.\d+)?/g) ?? []).map(Number);
    return r >= 230 && g >= 190 && b <= 80;
};

/** The blueprint's layout around this page: its root, the header/footer beside the page, and the
 * root's child that leads down to the page. */
interface Chrome {
    layout: HTMLElement;
    branch: HTMLElement;
    header: HTMLElement;
    footer?: HTMLElement;
}

// Everything brandHeader ever repainted - kept across fits AND across pages (the blueprint's bar
// outlives the page that measures it). Every fit first undoes the repaint and then looks for yellow
// again - but an element with a colour TRANSITION is then only on its way back to yellow, still
// reads as the brand colour, is skipped, and ends up yellow (reported: the avatar's block turned
// yellow whenever a tooltip, toast or menu made the page re-measure; on first load there is no
// transition, so it worked). What was yellow once is repainted again without asking for its
// current colour. Detached nodes are dropped on the way.
const brandedNodes = new Set<HTMLElement>();
const brandedPseudo = new Set<HTMLElement>();

// A block that belongs to the layout's flow - not one of our `position: fixed` header overlays, and
// not page content that merely sits above the page box (MobileHeaderBar, marked `data-pih-page`).
const isLayoutBlock = (node: Element): node is HTMLElement =>
    node instanceof HTMLElement && !node.hasAttribute('data-pih-page') && getComputedStyle(node).position !== 'fixed';

const isWide = (rect: DOMRect) => rect.width >= window.innerWidth * 0.6;

// Finds the page layout's header WITHOUT knowing its class names (the private @uag/react-core
// BaseLayout renders it): the innermost ancestor of this box that has, beside the branch leading
// here, a wide block ABOVE it is the layout root, that block its header, and a wide block below
// the branch (if any) its footer.
const findChrome = (el: HTMLElement): Chrome | undefined => {
    for (
        let branch = el;
        branch.parentElement && branch.parentElement !== document.body;
        branch = branch.parentElement
    ) {
        const layout = branch.parentElement;
        const own = branch.getBoundingClientRect();
        const siblings = [...layout.children].filter((child) => child !== branch).filter(isLayoutBlock);
        const header = siblings.find((child) => {
            const rect = child.getBoundingClientRect();
            return rect.height >= 30 && isWide(rect) && rect.bottom <= own.top + 1;
        });
        if (header) {
            const footer = siblings.find((child) => {
                const rect = child.getBoundingClientRect();
                return rect.height >= 20 && isWide(rect) && rect.top >= own.bottom - 1;
            });
            return { layout, branch, header, footer };
        }
    }
    return undefined;
};

// Makes the page exactly as tall as the window, so the document itself has nothing left to scroll
// and the header/footer can no longer be dragged away with it.
//
// The private @uag/react-core BaseLayout scrolls its CONTENT between a fixed header and footer.
// What scrolled the whole page on top of that (by ~60px, with the header travelling along, and the
// footer only reachable by scrolling) is the layout's outer frame: it is 100vh tall AND carries
// padding / a second, nested layout (Frame + page), so the sum exceeds the window. This sandbox
// cannot read BaseLayout, so instead of guessing class names the component looks at the real DOM:
// starting from its own box it walks up the ancestors and, outermost first, caps every one that
// reaches past the CONTENT BOX of its parent (padding and border excluded) to exactly that box.
// Measuring against the parent rather than the window is what makes nested paddings add up: an
// outer frame with 28px of padding top and bottom leaves the page inside it 56px short of the
// window, not 28 - the earlier version measured only the window and the immediate parent's bottom
// padding, so the page always ended up a few pixels too tall, the check below failed, everything
// was rolled back and the document kept scrolling (the reported scrollbar at the far right).
//
// Capping sets min-height to 0 as well: that beats a `min-h-screen` on the frame AND lets a flex
// item (the layout's content area between header and footer) shrink instead of pushing the
// footer out of the capped box. Whichever of the ancestors scrolls its own content (overflow
// auto/scroll; the innermost one is taken) is left to scroll; if none does, this component's own
// box becomes the scroller, capped to the space left under the header.
//
// Safe by construction: after capping, the document and every ancestor outside the scroller are
// measured again. Only if nothing really sticks out is the document's scrolling left switched off;
// otherwise (a phone in landscape where header and footer alone fill the screen) every change is
// undone and the page scrolls as it always did - nothing can end up cut off and unreachable.
//
// Re-fitted on window resize and whenever something outside the box is added or removed
// (header/footer chrome that renders after mount).
export const FitToViewport = ({ children, className }: { children: ReactNode; className?: string }) => {
    const ref = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) {
            return undefined;
        }
        const root = document.documentElement;
        const { body } = document;
        let saved: Saved[] = [];
        // Kept apart from `saved`: a fit that has to roll back its height caps must not undo the
        // flush (see flush below), and both are measured against the untouched layout.
        let flushSaved: Saved[] = [];
        let runs = 0;

        // Console: `__pihFit` says whether this component runs at all (undefined = it does not -
        // old build or page without it) and why the last run did what it did.
        const label = (node: HTMLElement) => `${node.tagName}.${String(node.className).slice(0, 60)}`;

        const record = (outcome: string, extra: Record<string, unknown> = {}) => {
            runs += 1;
            (window as unknown as { __pihFit?: unknown }).__pihFit = { runs, outcome, ...extra };
        };
        record('mounted');

        const remember = (list: Saved[], node: HTMLElement) => {
            if (!list.some((entry) => entry.node === node)) {
                const before = {} as Record<Prop, string>;
                PROPS.forEach((prop) => {
                    before[prop] = node.style[prop];
                });
                list.push({ node, before });
            }
        };

        const set = (node: HTMLElement, values: Partial<Record<Prop, string>>) => {
            remember(saved, node);
            Object.assign(node.style, values);
        };

        const restore = (list: Saved[]) => {
            // Newest first, so a node touched twice ends on its oldest value.
            [...list].reverse().forEach(({ node, before }) => {
                PROPS.forEach((prop) => {
                    node.style[prop] = before[prop];
                });
            });
        };

        const undo = () => {
            restore(saved);
            saved = [];
            root.classList.remove(ROOT_CLASS);
        };

        // Elements whose ::before/::after brandHeader repainted (via a data attribute, see App.css).
        let pseudoMarked: HTMLElement[] = [];


        const undoFlush = () => {
            restore(flushSaved);
            flushSaved = [];
            pseudoMarked.forEach((node) => {
                node.removeAttribute('data-pih-brand-pseudo');
                node.removeAttribute('data-pih-brand-corner');
            });
            pseudoMarked = [];
            root.style.removeProperty('--pih-brand-header-bg');
            SHIFT_VARS.forEach((name) => root.style.removeProperty(name));
            root.removeAttribute(BRAND_HEADER_ATTR);
        };

        const setStyle = (node: HTMLElement, values: Partial<Record<Prop, string>>) => {
            remember(flushSaved, node);
            Object.assign(node.style, values);
        };

        // Slightly rounded outer corners - top of the header, bottom of the footer (reported, as on
        // the Umdasch Group Identity page). Whichever element paints the bar's background has to
        // carry the radius, and that may be a child rather than the header element itself: so every
        // element with a background that sits in one of the two corners gets it.
        const roundCorners = (bar: HTMLElement, edge: 'top' | 'bottom', skipRight = false) => {
            const box = bar.getBoundingClientRect();
            [bar, ...bar.querySelectorAll<HTMLElement>('*')].forEach((node) => {
                const style = getComputedStyle(node);
                const transparent = style.backgroundColor === 'rgba(0, 0, 0, 0)' || style.backgroundColor === 'transparent';
                if (transparent) {
                    return;
                }
                const rect = node.getBoundingClientRect();
                const atEdge = edge === 'top' ? Math.abs(rect.top - box.top) < 1 : Math.abs(rect.bottom - box.bottom) < 1;
                if (!atEdge) {
                    return;
                }
                const values: Partial<Record<Prop, string>> = {};
                if (Math.abs(rect.left - box.left) < 1) {
                    values[edge === 'top' ? 'borderTopLeftRadius' : 'borderBottomLeftRadius'] = CHROME_RADIUS;
                }
                if (!skipRight && Math.abs(rect.right - box.right) < 1) {
                    values[edge === 'top' ? 'borderTopRightRadius' : 'borderBottomRightRadius'] = CHROME_RADIUS;
                }
                if (Object.keys(values).length > 0) {
                    setStyle(node, values);
                }
            });
        };

        // A brand other than DOKA (branding.ts): repaint the blueprint's yellow bar in the brand
        // colour and hide the DOKA logo - BrandHeaderBar adds the brand's own. Repainting the REAL
        // bar instead of covering it keeps whatever else the blueprint puts there (the user's avatar
        // and its menu) visible and clickable; the former full-width cover hid them. Found by
        // colour and position rather than class names (the bar is the private @uag BaseLayout's):
        // every element painted DOKA yellow gets the brand colour, every image/SVG in the bar's left
        // half - the logo - is hidden. Returns whether anything yellow was found to repaint.
        //
        // Not only the bar's own subtree: the avatar sits on a yellow block of its own that is NOT
        // inside the element found as the header (reported: a yellow square left behind the avatar
        // on dev). So the elements stacked at points along the bar's middle are checked as well,
        // and their ::before/::after too (painted through a stylesheet rule, see App.css).
        //
        // Across the WHOLE window width, not just the bar's: in the catalog the bar ends where the
        // content's scrollbar begins, while the avatar's block is pinned to the window's right edge
        // - beyond the bar, so sampling only the bar's own width left it yellow (reported). Such a
        // block, repainted, also takes over the bar's rounded top-right corner.
        let outerCornerTaken = false;
        const brandHeader = (bar: HTMLElement): boolean => {
            const box = bar.getBoundingClientRect();
            let painted = false;
            outerCornerTaken = false;
            root.style.setProperty('--pih-brand-header-bg', brand.headerBackground);
            const sampled = new Set<Element>();
            const windowRight = root.clientWidth;
            for (let x = 2; x < windowRight; x += 24) {
                for (const y of [box.top + 2, box.top + (box.height / 2), box.bottom - 2]) {
                    document.elementsFromPoint(x, y).forEach((hit) => sampled.add(hit));
                }
            }
            const candidates = new Set<HTMLElement>([bar, ...bar.querySelectorAll<HTMLElement>('*')]);
            sampled.forEach((hit) => {
                // Never the page's own frame around everything (it reaches into the bar's rows too).
                if (hit instanceof HTMLElement && hit !== root && hit !== document.body && !hit.contains(bar)) {
                    candidates.add(hit);
                }
            });
            // Hit-testing alone is not enough: it skips anything with `pointer-events: none`, and
            // on dev the avatar's block turned yellow again as soon as a tooltip or toast made the
            // page re-measure (reported). So every element outside the page itself that sits in the
            // bar's band - no taller than the bar - is checked as well, hit or not.
            const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT, {
                acceptNode: (node) =>
                    node === el || node === bar ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT
            });
            for (let node = walker.nextNode(); node; node = walker.nextNode()) {
                if (!(node instanceof HTMLElement) || node.contains(bar)) {
                    continue;
                }
                const rect = node.getBoundingClientRect();
                if (rect.top < box.bottom - 1 && rect.bottom > box.top + 1 && rect.height <= box.height + 4) {
                    candidates.add(node);
                }
            }
            [brandedNodes, brandedPseudo].forEach((known) =>
                known.forEach((node) => (node.isConnected ? candidates.add(node) : known.delete(node)))
            );
            // ...and the bar's own ancestors: where the bar ends short of the window's right edge
            // (the catalog's scrollbar column), what shows beside it is the layout's frame behind
            // the bar - yellow, if the blueprint paints it so. Repainting it only changes that gap:
            // everywhere else the page's own areas cover it.
            for (let node = bar.parentElement; node && node !== root && node !== document.body; node = node.parentElement) {
                candidates.add(node);
            }
            candidates.forEach((node) => {
                const style = getComputedStyle(node);
                const rect = node.getBoundingClientRect();
                const outerCorner =
                    !bar.contains(node) && Math.abs(rect.top - box.top) < 1 && rect.right >= windowRight - 1;
                if (brandedNodes.has(node) || isDokaYellow(style.backgroundColor)) {
                    brandedNodes.add(node);
                    setStyle(node, {
                        backgroundColor: brand.headerBackground,
                        ...(outerCorner ? { borderTopRightRadius: CHROME_RADIUS } : {})
                    });
                    outerCornerTaken ||= outerCorner;
                    painted = true;
                }
                if (
                    brandedPseudo.has(node) ||
                    isDokaYellow(getComputedStyle(node, '::before').backgroundColor) ||
                    isDokaYellow(getComputedStyle(node, '::after').backgroundColor)
                ) {
                    brandedPseudo.add(node);
                    node.setAttribute('data-pih-brand-pseudo', '');
                    if (outerCorner) {
                        node.setAttribute('data-pih-brand-corner', '');
                        outerCornerTaken = true;
                    }
                    pseudoMarked.push(node);
                    painted = true;
                }
                if (!bar.contains(node)) {
                    return;
                }
                const isPicture =
                    node instanceof HTMLImageElement ||
                    node instanceof SVGSVGElement ||
                    (style.backgroundImage !== 'none' && style.backgroundImage !== '');
                if (isPicture && rect.width > 0 && rect.right <= box.left + (box.width / 2)) {
                    setStyle(node, { visibility: 'hidden' });
                }
            });
            return painted;
        };

        // Header and footer flush with the window (reported: "Im Desktop modus sind header und
        // footer mit 'margin' eingerückt. Header und footer sollen bündig erscheinen"). The inset
        // comes from the blueprint's two nested layouts - Frame's BaseLayout pads its content, and
        // the page's own BaseLayout, header and footer included, sits inside that padding. So the
        // page layout's root and everything above it lose their padding, margins and max-width
        // (a centred container would inset just the same), the header/footer their outer margins.
        //
        // The page content must not move with them: whatever left/right inset it lost is given back
        // as padding on the layout's branch towards it, so only the chrome changes. The header
        // overlays (title, language, Merkliste) follow the bar through the shift variables, see
        // headerLayout.HEADER_SHIFT_*_VAR. `window.__pihFlush` tells what was found and moved.
        const flush = () => {
            const chrome = findChrome(el);
            if (!chrome) {
                (window as unknown as { __pihFlush?: unknown }).__pihFlush = { outcome: 'no-header-found' };
                return;
            }
            const { layout, branch, header, footer } = chrome;
            const headerBefore = header.getBoundingClientRect();
            const contentBefore = el.getBoundingClientRect();
            const touch = (node: HTMLElement, keys: readonly Prop[]) => {
                const style = getComputedStyle(node);
                const values: Partial<Record<Prop, string>> = {};
                keys.forEach((prop) => {
                    if (prop === 'maxWidth' ? style.maxWidth !== 'none' : px(style[prop]) !== 0) {
                        values[prop] = prop === 'maxWidth' ? 'none' : '0';
                    }
                });
                if (Object.keys(values).length > 0) {
                    remember(flushSaved, node);
                    Object.assign(node.style, values);
                }
            };
            for (let node: HTMLElement | null = layout; node && node !== root; node = node.parentElement) {
                touch(node, [
                    'paddingTop',
                    'paddingRight',
                    'paddingBottom',
                    'paddingLeft',
                    'marginTop',
                    'marginRight',
                    'marginBottom',
                    'marginLeft',
                    'maxWidth'
                ]);
            }
            touch(header, ['marginTop', 'marginRight', 'marginLeft', 'maxWidth']);
            if (footer) {
                touch(footer, ['marginBottom', 'marginRight', 'marginLeft', 'maxWidth']);
            }

            const contentAfter = el.getBoundingClientRect();
            const lostLeft = contentBefore.left - contentAfter.left;
            const lostRight = contentAfter.right - contentBefore.right;
            if (lostLeft > 0.5 || lostRight > 0.5) {
                const style = getComputedStyle(branch);
                remember(flushSaved, branch);
                Object.assign(branch.style, {
                    boxSizing: 'border-box',
                    paddingLeft: `${px(style.paddingLeft) + Math.max(0, lostLeft)}px`,
                    paddingRight: `${px(style.paddingRight) + Math.max(0, lostRight)}px`
                });
            }

            const headerAfter = header.getBoundingClientRect();
            if (brand.overridesHeader && brandHeader(header)) {
                root.setAttribute(BRAND_HEADER_ATTR, '');
            }
            // The bar's own right corner stays square where a repainted block beyond it took over
            // the outer corner - rounded, it would leave a notch between the two.
            roundCorners(header, 'top', outerCornerTaken);
            if (footer) {
                roundCorners(footer, 'bottom');
            }
            root.style.setProperty(HEADER_BOX_LEFT_VAR, `${headerAfter.left}px`);
            root.style.setProperty(HEADER_BOX_TOP_VAR, `${headerAfter.top}px`);
            root.style.setProperty(HEADER_BOX_HEIGHT_VAR, `${headerAfter.height}px`);
            root.style.setProperty(HEADER_SHIFT_LEFT_VAR, `${headerAfter.left - headerBefore.left}px`);
            root.style.setProperty(HEADER_SHIFT_TOP_VAR, `${headerAfter.top - headerBefore.top}px`);
            root.style.setProperty(HEADER_SHIFT_RIGHT_VAR, `${headerAfter.right - headerBefore.right}px`);
            (window as unknown as { __pihFlush?: unknown }).__pihFlush = {
                outcome: 'flushed',
                header: label(header),
                footer: footer ? label(footer) : undefined,
                headerBefore: [headerBefore.left, headerBefore.top, window.innerWidth - headerBefore.right],
                headerAfter: [headerAfter.left, headerAfter.top, window.innerWidth - headerAfter.right],
                touched: flushSaved.map(({ node }) => label(node))
            };
        };

        const fit = () => {
            undo();
            undoFlush();
            flush();
            // Ancestors between <body> and this box, outermost first.
            const chain: HTMLElement[] = [];
            for (let node = el.parentElement; node && node !== body; node = node.parentElement) {
                chain.unshift(node);
            }
            // The layout's own content scroller, if it has one (innermost wins): its overflow is
            // content and legitimate, everything outside it has to fit the window exactly.
            const ownScroller = [...chain].reverse().find(isScrollContainer);
            const scroller = ownScroller ?? el;
            const outside = ownScroller ? chain.slice(0, chain.indexOf(ownScroller)) : chain;
            const toFit = ownScroller ? [...outside, ownScroller] : [...chain, el];

            // Anything sticking out of the window, or out of an ancestor that does not scroll
            // (overflow visible/hidden would clip or spill it)?
            const sticksOut = () =>
                [root, body, ...outside].some((node) => overflowOf(node).some((amount) => amount > 1));

            // Measured with the document scrollbar already gone (the class hides it): a box capped
            // to the width next to a scrollbar would stay that much too narrow once it disappears.
            root.classList.add(ROOT_CLASS);
            const viewport = [root.clientWidth, root.clientHeight];
            if (!sticksOut()) {
                undo();
                record('no-overflow', { viewport });
                return;
            }
            const before = overflowOf(root);
            const capped: HTMLElement[] = [];
            // Outermost first: capping an outer frame moves what is inside it, so the inner ones
            // are measured against the already corrected layout.
            toFit.forEach((node) => {
                const parent = node.parentElement;
                if (!parent) {
                    return;
                }
                const parentRect = parent.getBoundingClientRect();
                const parentStyle = getComputedStyle(parent);
                const style = getComputedStyle(node);
                const rect = node.getBoundingClientRect();
                // Where the parent's content box ends - never past the window.
                const limitBottom = Math.min(
                    viewport[1],
                    parentRect.bottom - px(parentStyle.paddingBottom) - px(parentStyle.borderBottomWidth)
                );
                const limitRight = Math.min(
                    viewport[0],
                    parentRect.right - px(parentStyle.paddingRight) - px(parentStyle.borderRightWidth)
                );
                if (rect.bottom + px(style.marginBottom) > limitBottom + 0.5) {
                    set(node, {
                        boxSizing: 'border-box',
                        // min-height (Tailwind's min-h-screen, a flex item's automatic minimum)
                        // beats max-height - drop it, or the cap would silently do nothing.
                        minHeight: '0',
                        maxHeight: `${Math.max(0, limitBottom - rect.top - px(style.marginBottom))}px`
                    });
                    capped.push(node);
                }
                // The frame is also wider than the window (100vw plus padding): same cap, sideways.
                if (rect.right + px(style.marginRight) > limitRight + 0.5) {
                    set(node, {
                        boxSizing: 'border-box',
                        minWidth: '0',
                        maxWidth: `${Math.max(0, limitRight - rect.left - px(style.marginRight))}px`
                    });
                    if (!capped.includes(node)) {
                        capped.push(node);
                    }
                }
            });
            // No scroller in the layout at all: this box takes the job, so long content scrolls
            // here, under a header and above a footer that stay put.
            if (!ownScroller) {
                set(el, { overflowY: 'auto', minHeight: '0' });
            }
            const fits = !sticksOut();
            record(fits ? 'fitted' : 'rolled-back', {
                viewport,
                overflowBefore: before,
                overflowAfter: overflowOf(root),
                scroller: label(scroller),
                capped: capped.map(label),
                chain: chain.map(label)
            });
            if (!fits) {
                undo();
            }
        };

        let frame = 0;
        const scheduleFit = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(fit);
        };

        fit();
        window.addEventListener('resize', scheduleFit);
        window.addEventListener('load', scheduleFit);
        // The catalog fills in after mount (data, images, header/footer chrome): measure again once
        // things have settled, and whenever the content box itself changes size. A fit that finds
        // nothing to do changes nothing, so this cannot ping-pong with the observer.
        const settle = [250, 1000, 2500].map((ms) => window.setTimeout(scheduleFit, ms));
        const resizes = new ResizeObserver(scheduleFit);
        resizes.observe(el);
        // Mutations INSIDE the box (every list render) cannot change what surrounds it, and fit()
        // forces layout - so only changes elsewhere in the page count.
        const mutations = new MutationObserver((records) => {
            if (records.some((entry) => !el.contains(entry.target))) {
                scheduleFit();
            }
        });
        mutations.observe(document.body, { childList: true, subtree: true });
        // Web fonts change text metrics, i.e. the header/footer heights.
        void document.fonts?.ready.then(scheduleFit);

        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('resize', scheduleFit);
            window.removeEventListener('load', scheduleFit);
            settle.forEach((id) => window.clearTimeout(id));
            resizes.disconnect();
            mutations.disconnect();
            undo();
            undoFlush();
        };
    }, []);

    return (
        <div ref={ref} className={className}>
            {children}
        </div>
    );
};
